import React, { useState, useRef, useEffect } from "react";
import { 
  FaServer, 
  FaNetworkWired, 
  FaBoxArchive, 
  FaCube, 
  FaUser, 
  FaPlus, 
  FaArrowDown,
  FaTrash,
  FaCheck,
  FaXmark
} from "react-icons/fa6";
import { Header } from "../../../../components/Header";

// Urutan Hirarki Jaringan FO
const HIERARCHY_ORDER = ['pop', 'router', 'olt', 'odc_pusat', 'odc', 'odp', 'customer'];

// Configuration Warna & Icon
const NODE_CONFIG = {
  pop: { 
    label: "POP / Core", 
    color: "bg-purple-100 text-purple-700 border-purple-300 shadow-purple-100", 
    icon: FaServer 
  },
  router: { 
    label: "Nas / Mikrotik", 
    color: "bg-blue-100 text-blue-700 border-blue-300 shadow-blue-100", 
    icon: FaNetworkWired 
  },
  olt: { 
    label: "OLT", 
    color: "bg-amber-100 text-amber-700 border-amber-300 shadow-amber-100", 
    icon: FaServer 
  },
  odc_pusat: { 
    label: "ODC Pusat", 
    color: "bg-indigo-100 text-indigo-700 border-indigo-300 shadow-indigo-100", 
    icon: FaBoxArchive 
  },
  odc: { 
    label: "Sub ODC", 
    color: "bg-teal-100 text-teal-700 border-teal-300 shadow-teal-100", 
    icon: FaCube 
  },
  odp: { 
    label: "ODP", 
    color: "bg-emerald-100 text-emerald-700 border-emerald-300 shadow-emerald-100", 
    icon: FaCube 
  },
  customer: { 
    label: "Pelanggan", 
    color: "bg-rose-100 text-rose-700 border-rose-300 shadow-rose-100", 
    icon: FaUser 
  },
};

export const DiagramManagement = () => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [draggingNode, setDraggingNode] = useState(null);
  const containerRef = useRef(null);
  const [openSidebar, setOpenSidebar] = useState(false);

  // State Modal Tambah Node
  const [showAddModal, setShowAddModal] = useState(false);
  const [targetNode, setTargetNode] = useState(null);
  const [addDirection, setAddDirection] = useState('child');
  const [newNodeLabel, setNewNodeLabel] = useState("");
  const [newNodeType, setNewNodeType] = useState("customer");

  // State Edit Label Inline (Klik 2x)
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  // Data Initial Nodes Sesuai Hirarki
  const [nodes, setNodes] = useState([
    { id: 'pop', label: 'pop', type: 'pop', x: 8, y: 48 },
    { id: 'mik-1', label: 'Nas / Mikrotik 1', type: 'router', x: 22, y: 15 },
    { id: 'mik-2', label: 'Nas / Mikrotik 2', type: 'router', x: 22, y: 48 },
    { id: 'olt-1', label: 'OLT 1', type: 'olt', x: 36, y: 44 },
    { id: 'odc-p1', label: 'ODC PUSAT 1', type: 'odc_pusat', x: 50, y: 30 },
    { id: 'sub-1', label: 'ODC 4', type: 'odc', x: 64, y: 44 },
    { id: 'odp-1', label: 'ODP 1', type: 'odp', x: 76, y: 44 },
    { id: 'cust-1', label: 'Pelanggan 1', type: 'customer', x: 88, y: 36 },
    { id: 'cust-2', label: 'Pelanggan 2', type: 'customer', x: 88, y: 44 },
    { id: 'cust-3', label: 'Pelanggan 3', type: 'customer', x: 88, y: 52 },
  ]);

  const [links, setLinks] = useState([
    { from: 'pop', to: 'mik-1' },
    { from: 'pop', to: 'mik-2' },
    { from: 'mik-2', to: 'olt-1' },
    { from: 'olt-1', to: 'odc-p1' },
    { from: 'odc-p1', to: 'sub-1' },
    { from: 'sub-1', to: 'odp-1' },
    { from: 'odp-1', to: 'cust-1' },
    { from: 'odp-1', to: 'cust-2' },
    { from: 'odp-1', to: 'cust-3' },
  ]);

  // Kurva Bezier Smooth
  const calculateBezierPath = (x1, y1, x2, y2) => {
    const dx = Math.abs(x2 - x1) * 0.5;
    return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
  };

  // Drag Handlers
  const handleNodeMouseDown = (e, node) => {
    e.stopPropagation();
    if (isEditMode) setDraggingNode(node);
  };

  const handleMouseMove = (e) => {
    if (!isEditMode || !draggingNode || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    let x = ((e.clientX - rect.left) / rect.width) * 100;
    let y = ((e.clientY - rect.top) / rect.height) * 100;

    x = Math.max(3, Math.min(97, x));
    y = Math.max(3, Math.min(97, y));

    setNodes(prev => prev.map(n => n.id === draggingNode.id ? { ...n, x, y } : n));
  };

  const handleMouseUp = () => setDraggingNode(null);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isEditMode, draggingNode]);

  // LOGIKA UTAMA: Penentuan Tipe Otomatis Berdasarkan Hirarki
  const handleOpenAddModal = (e, node, direction) => {
    e.stopPropagation();
    e.preventDefault();
    setTargetNode(node);
    setAddDirection(direction);

    let calculatedType = node.type;

    if (direction === 'sibling') {
      // Sibling (↓ Panah Bawah) -> Tipe SAMA PERSIS dengan induknya
      calculatedType = node.type;
    } else {
      // Child (+ Kanan) -> Tipe SATU TINGKAT DI BAWAHNYA
      const currentIndex = HIERARCHY_ORDER.indexOf(node.type);
      if (currentIndex !== -1 && currentIndex < HIERARCHY_ORDER.length - 1) {
        calculatedType = HIERARCHY_ORDER[currentIndex + 1];
      } else {
        calculatedType = 'customer'; // Default mentok di pelanggan
      }
    }

    setNewNodeType(calculatedType);
    
    // Auto-generate nama default sesuai tipe
    const defaultName = NODE_CONFIG[calculatedType]?.label || "Perangkat Baru";
    setNewNodeLabel(`${defaultName} Baru`);
    setShowAddModal(true);
  };

  // Eksekusi Tambah Node
  const handleAddNode = (e) => {
    e.preventDefault();
    if (!newNodeLabel.trim() || !targetNode) return;

    const newId = `node-${Date.now()}`;
    let newX = targetNode.x;
    let newY = targetNode.y;

    if (addDirection === 'child') {
      // Tambah Child (Geser ke Kanan)
      newX = Math.min(targetNode.x + 12, 95);
      newY = Math.max(5, Math.min(95, targetNode.y + (Math.random() * 4 - 2)));
    } else {
      // Tambah Sibling (Geser Sejajar ke Bawah)
      newX = targetNode.x;
      newY = Math.min(targetNode.y + 7, 95);
    }

    const newNode = {
      id: newId,
      label: newNodeLabel.trim(),
      type: newNodeType,
      x: newX,
      y: newY,
    };

    setNodes(prev => [...prev, newNode]);

    if (addDirection === 'child') {
      // Koneksi langsung dari targetNode
      setLinks(prev => [...prev, { from: targetNode.id, to: newId }]);
    } else {
      // Sibling: cari parent dari targetNode untuk disambungkan
      const parentLink = links.find(l => l.to === targetNode.id);
      if (parentLink) {
        setLinks(prev => [...prev, { from: parentLink.from, to: newId }]);
      }
    }

    setShowAddModal(false);
  };

  // Hapus Node
  const handleDeleteNode = (e, nodeId) => {
    e.stopPropagation();
    e.preventDefault();
    setNodes(prev => prev.filter(n => n.id !== nodeId));
    setLinks(prev => prev.filter(l => l.from !== nodeId && l.to !== nodeId));
  };

  // Edit Label Inline (Klik 2x)
  const handleSaveInlineEdit = (nodeId) => {
    if (editingLabel.trim()) {
      setNodes(prev => prev.map(n => n.id === nodeId ? { ...n, label: editingLabel.trim() } : n));
    }
    setEditingNodeId(null);
  };

  return (
    <div className="w-full h-screen flex flex-col bg-slate-50 font-sans select-none">
      <Header toggleSidebar={() => setOpenSidebar(!openSidebar)}>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Mindmap Topologi Jaringan</h2>
          <p className="text-xs text-slate-500">Tipe perangkat otomatis teratur sesuai hirarki FO</p>
        </div>
        <button 
          onClick={() => setIsEditMode(!isEditMode)}
          className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all ${
            isEditMode 
              ? 'bg-amber-500 text-white border-amber-600 shadow-md' 
              : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
          }`}
        >
          {isEditMode ? 'Mode Drag: ON' : 'Aktifkan Mode Drag'}
        </button>
      </Header>

      <div 
        ref={containerRef}
        className="relative flex-1 w-full h-full overflow-hidden bg-[#fafafa]"
        style={{
          backgroundImage: 'radial-gradient(#d1d5db 1.2px, transparent 1.2px)',
          backgroundSize: '24px 24px'
        }}
      >
        {/* Layer Garis Koneksi */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
          {links.map((link, index) => {
            const fromNode = nodes.find(n => n.id === link.from);
            const toNode = nodes.find(n => n.id === link.to);
            if (!fromNode || !toNode) return null;

            return (
              <path
                key={index}
                d={calculateBezierPath(fromNode.x, fromNode.y, toNode.x, toNode.y)}
                fill="none"
                stroke="#94a3b8"
                strokeWidth="0.4"
                strokeLinecap="round"
              />
            );
          })}
        </svg>

        {/* Layer Node */}
        {nodes.map((node) => {
          const config = NODE_CONFIG[node.type] || NODE_CONFIG.odc;
          const IconComponent = config.icon;
          const isEditing = editingNodeId === node.id;
          const isCustomer = node.type === 'customer'; // Cek apakah node adalah pelanggan

          return (
            <div
              key={node.id}
              onMouseDown={(e) => handleNodeMouseDown(e, node)}
              onDoubleClick={(e) => {
                e.stopPropagation();
                setEditingNodeId(node.id);
                setEditingLabel(node.label);
              }}
              className={`
                absolute transform -translate-x-1/2 -translate-y-1/2
                flex items-center gap-2 px-3 py-1.5 rounded-full border shadow-sm backdrop-blur-md
                transition-all duration-150 group z-10 hover:z-30 hover:scale-105
                ${config.color}
                ${isEditMode ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}
              `}
              style={{ left: `${node.x}%`, top: `${node.y}%` }}
            >
              <IconComponent className="text-sm shrink-0 pointer-events-none" />
              
              {isEditing ? (
                <div className="flex items-center gap-1" onMouseDown={(e) => e.stopPropagation()}>
                  <input
                    type="text"
                    autoFocus
                    value={editingLabel}
                    onChange={(e) => setEditingLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveInlineEdit(node.id);
                      if (e.key === 'Escape') setEditingNodeId(null);
                    }}
                    className="px-1 py-0.5 text-xs border rounded bg-white text-slate-800 focus:outline-none w-24"
                  />
                  <button 
                    onClick={() => handleSaveInlineEdit(node.id)}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                  >
                    <FaCheck size={10} />
                  </button>
                </div>
              ) : (
                <span className="text-xs font-semibold whitespace-nowrap pointer-events-none">
                  {node.label}
                </span>
              )}

              {/* ACTION TOOLBAR HOVER */}
              {!isEditing && (
                <>
                  {/* Tombol Hapus (Atas) */}
                  <button
                    type="button"
                    title="Hapus Node"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleDeleteNode(e, node.id)}
                    className="absolute -top-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 p-1 rounded-full bg-rose-500 text-white hover:bg-rose-600 shadow-md cursor-pointer"
                  >
                    <FaTrash size={8} />
                  </button>

                  {/* Tombol Add Child (+ Kanan) - DISENTUK/DISUBSTITUSI jika Pelanggan */}
                  {!isCustomer && (
                    <button
                      type="button"
                      title={`Tambah ${NODE_CONFIG[HIERARCHY_ORDER[HIERARCHY_ORDER.indexOf(node.type) + 1]]?.label || 'Sub'}`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => handleOpenAddModal(e, node, 'child')}
                      className="absolute -right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 p-1.5 rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-md cursor-pointer hover:scale-110"
                    >
                      <FaPlus size={9} />
                    </button>
                  )}

                  {/* Tombol Add Sibling (↓ Panah Bawah) -> Otomatis SAMA TIPE */}
                  <button
                    type="button"
                    title={`Tambah ${config.label} Sejajar di Bawah`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => handleOpenAddModal(e, node, 'sibling')}
                    className="absolute -bottom-3 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 p-1.5 rounded-full bg-blue-500 text-white hover:bg-blue-600 shadow-md cursor-pointer hover:scale-110"
                  >
                    <FaArrowDown size={9} />
                  </button>
                </>
              )}
            </div>
          );
        })}

        {/* Legend Warna */}
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur border border-slate-200 rounded-xl p-3 shadow-lg flex flex-wrap gap-3 text-xs z-20">
          {Object.entries(NODE_CONFIG).map(([key, item]) => {
            const Icon = item.icon;
            return (
              <div key={key} className="flex items-center gap-1.5">
                <span className={`p-1 rounded-full border ${item.color}`}>
                  <Icon size={10} />
                </span>
                <span className="text-slate-600 font-medium">{item.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Dialog Tambah Node */}
      {showAddModal && (
        <div 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <form 
            onSubmit={handleAddNode}
            className="bg-white rounded-2xl p-5 w-80 shadow-2xl border border-slate-100 flex flex-col gap-4 animate-in fade-in zoom-in-95"
          >
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-sm font-bold text-slate-800">
                  {addDirection === 'child' ? 'Tambah Cabang Hirarki' : 'Tambah Sejajar (Kebawah)'}
                </h3>
                <p className="text-xs text-slate-500">
                  Induk: <span className="font-semibold text-blue-600">{targetNode?.label}</span>
                </p>
              </div>
              <button 
                type="button" 
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg"
              >
                <FaXmark size={14} />
              </button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1">
                  Nama Node / Perangkat
                </label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={newNodeLabel}
                  onChange={(e) => setNewNodeLabel(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider block mb-1">
                  Tipe Perangkat (Otomatis)
                </label>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-slate-50 text-xs font-semibold text-slate-700">
                  <span className={`p-1 rounded-full ${NODE_CONFIG[newNodeType]?.color}`}>
                    {React.createElement(NODE_CONFIG[newNodeType]?.icon || FaCube, { size: 12 })}
                  </span>
                  <span>{NODE_CONFIG[newNodeType]?.label}</span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowAddModal(false)}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition shadow-sm"
              >
                Simpan Node
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};